import { ShirtIcon } from "lucide-react";

import { DetailSection } from "@/components/show";
import { Badge } from "@/components/ui/badge";
import type {
  ILineupPlayer,
  IMatchLineups,
  ITeamLineup,
} from "@/types/match-details";

/** A lineup with no player on either list is nothing to show. */
const hasPlayers = (lineup?: ITeamLineup | null) =>
  !!lineup && (!!lineup.startXI?.length || !!lineup.substitutes?.length);

export function lineupsHaveData(lineups?: IMatchLineups | null): boolean {
  return hasPlayers(lineups?.home) || hasPlayers(lineups?.away);
}

function PlayerList({
  title,
  players,
}: {
  title: string;
  players: ILineupPlayer[];
}) {
  if (!players.length) return null;
  return (
    <div className="flex flex-col gap-1.5">
      <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </h4>
      <ul className="flex flex-col gap-1">
        {players.map((p, i) => (
          <li
            key={p.providerId ?? i}
            className="flex items-baseline gap-2 text-sm"
          >
            <span className="w-6 text-right font-mono text-xs text-muted-foreground">
              {p.number ?? ""}
            </span>
            <span>{p.name ?? "—"}</span>
            {p.position && (
              <span className="text-xs text-muted-foreground">
                {p.position}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TeamColumn({
  name,
  lineup,
}: {
  name: string;
  lineup?: ITeamLineup | null;
}) {
  if (!hasPlayers(lineup)) return <div />;
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold">{name}</h3>
        {lineup?.formation && (
          <Badge variant="outline">{lineup.formation}</Badge>
        )}
      </div>
      <PlayerList title="Starting XI" players={lineup?.startXI ?? []} />
      <PlayerList title="Substitutes" players={lineup?.substitutes ?? []} />
    </div>
  );
}

/** Football only: formation, starting XI and substitutes for each side that has a sheet. */
export function MatchLineupsCard({
  lineups,
  homeName,
  awayName,
}: {
  lineups: IMatchLineups;
  homeName: string;
  awayName: string;
}) {
  return (
    <DetailSection title="Lineups" icon={<ShirtIcon className="size-4" />}>
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        <TeamColumn name={homeName} lineup={lineups.home} />
        <TeamColumn name={awayName} lineup={lineups.away} />
      </div>
    </DetailSection>
  );
}
